/**
 * 临时 API 封装：AI 助手
 *
 * 后端 PR (wyr-system/wyr.crm.api#184) 合并部署后，请运行 `nad` 同步重新生成
 * `src/api.ts`，把下方 aiAssistantApi 的 CRUD 方法替换为生成出来的真实绑定。
 * chatStream 是 SSE 流式接口，NAD Runtime（XHR）不支持流式，需长期保留 fetch 实现。
 */
import { Runtime } from '../api';
import { apiHost } from '../apis/request';

export type AiMessageRole = 'USER' | 'ASSISTANT' | 'TOOL';
export type AiMessageStatus = 'GENERATING' | 'COMPLETED' | 'FAILED' | 'INTERRUPTED';

export interface AiConversation {
  id: string;
  title: string;
  lastMessageAt?: string;
  createdAt?: string;
}

export interface AiMessage {
  id?: string;
  role: AiMessageRole;
  content?: string;
  /** 深度思考内容（reasoning_content），折叠区展示 */
  reasoningContent?: string;
  /** assistant 发起的工具调用原始 JSON：[{id,name,arguments}] */
  toolCalls?: string;
  /** TOOL 消息：对应的 tool_call id */
  toolCallId?: string;
  toolName?: string;
  charts?: string;
  /** 技能装配轨迹 JSON {total,matched,names,skills}，落在每次提问首轮的 assistant 消息上 */
  skillTrace?: string;
  status?: AiMessageStatus;
  errorMessage?: string;
  /** 实际使用的模型 */
  model?: string;
  durationMs?: number;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  cachedTokens?: number;
  createdAt?: string;
}

export interface AiConversationPage {
  total: number;
  size: number;
  page: number;
  list: AiConversation[];
}

export type AiThinkingLevel = 'off' | 'low' | 'medium' | 'high';

/** AI 助手技能：管理员自定义的场景化提示词，开启后拼进 system prompt */
export interface AiChatSkill {
  name?: string;
  /** 适用场景说明 */
  description?: string;
  /** 技能提示词 */
  prompt?: string;
  /** 示例提问，空会话时作为引导按钮展示 */
  examples?: string[];
  enabled?: boolean;
}

/** 与后端 AiChatSkill / AiChatConfig 的校验上限保持一致 */
export const AI_SKILL_LIMITS = {
  maxSkills: 20,
  maxNameChars: 30,
  maxDescriptionChars: 200,
  maxPromptChars: 2000,
  maxExamples: 5,
  maxExampleChars: 100,
};

/** AI 助手 LLM 配置（仅 superadmin 指定账号在「系统配置」页读写） */
export interface AiChatConfig {
  enabled?: boolean;
  /** 功能总开关：默认 false 所有人不可见；开启后仅 superadmin 可使用 */
  visibleToSuperAdmin?: boolean;
  key?: string;
  base_url?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  /** off → enable_thinking=false；low/medium/high → enable_thinking=true + reasoning_effort（兼容 DeepSeek v4） */
  thinkingLevel?: AiThinkingLevel;
  systemPrompt?: string;
  extraBody?: Record<string, any>;
  connectTimeoutMs?: number;
  readTimeoutMs?: number;
  maxHistoryMessages?: number;
  maxHistoryChars?: number;
  sseTimeoutMs?: number;
  heartbeatIntervalMs?: number;
  /** 自定义技能列表 */
  skills?: AiChatSkill[];
}

/** 空会话展示的示例提问；skillName 为空表示内置兜底问题 */
export interface AiStarterQuestion {
  question: string;
  skillName?: string | null;
}

/** system prompt 预览（仅 superadmin 可读） */
export interface AiSystemPromptPreview {
  /** 内置默认提示词 */
  builtinSystemPrompt?: string;
  /** 当前配置实际下发给模型的完整 prompt（数据权限段用示意范围渲染） */
  effectiveSystemPrompt?: string;
  usingCustomPrompt?: boolean;
  enabledSkillCount?: number;
  starterQuestions?: AiStarterQuestion[];
}

export const aiChatPromptPreviewApi = {
  async get() {
    return new Runtime<AiSystemPromptPreview>()
      .open('GET', '/system/ai_chat_prompt_preview')
      .execute();
  },
};

export const aiChatConfigApi = {
  async get() {
    return new Runtime<AiChatConfig>().open('GET', '/system/ai_chat_config').execute();
  },
  async update(dto: AiChatConfig) {
    return new Runtime<void>()
      .open('POST', '/system/ai_chat_config')
      .addRequestBody(dto)
      .execute();
  },
};

/** AI 技能/提示词跨公司同步：单个目标公司的结果 */
export interface AiChatSkillSyncCompanyResult {
  companyId?: number | string;
  /** 目标公司名，解析不到为 null */
  companyName?: string | null;
  success?: boolean;
  /** 失败原因（success=false 时填充） */
  error?: string | null;
}

/** AI 技能/提示词跨公司同步整体结果 */
export interface AiChatSkillSyncResult {
  /** 本次同步的技能条数（含未启用的，目标端整体覆盖） */
  totalSkills?: number;
  /** 是否同步了自定义 System Prompt（false = 目标覆盖后回落内置默认） */
  customPrompt?: boolean;
  results?: AiChatSkillSyncCompanyResult[];
}

export const aiChatSkillSyncApi = {
  /** 把本系统 AI 助手技能与自定义 System Prompt 整体覆盖到目标公司（仅超管） */
  async sync(companyId: string | number) {
    return new Runtime<AiChatSkillSyncResult>()
      .open('POST', `/company/${companyId}/ai_chat/skill/sync`)
      .execute();
  },
};

/** 已注册的 AI 工具信息（系统配置页展示） */
export interface AiToolInfo {
  name: string;
  description: string;
  parameters: Record<string, any>;
  beanClass: string;
}

export const aiChatToolsApi = {
  async list() {
    return new Runtime<AiToolInfo[]>().open('GET', '/system/ai_chat_tools').execute();
  },
};

/** 导出 PDF 的授权凭据，由服务端签发（前端不自己拼水印） */
export interface AiExportTicket {
  /** 平铺在每页上的水印：姓名-手机尾号-日期 */
  watermark: string;
  /** 建议的下载文件名（含 .pdf） */
  fileName: string;
  /** 会话标题，渲染在 PDF 页眉 */
  title: string;
  exportedAt?: string;
}

export const aiAssistantApi = {
  /** 空会话的示例提问：已开启技能自带的示例，没有则内置兜底 */
  async listStarters(companyId: string | number) {
    return new Runtime<AiStarterQuestion[]>()
      .open('GET', `/company/${companyId}/ai-assistant/starters`)
      .execute();
  },
  async listConversations(companyId: string | number, current = 1, pageSize = 100) {
    return new Runtime<AiConversationPage>()
      .open(
        'GET',
        `/company/${companyId}/ai-assistant/conversations?current=${current}&pageSize=${pageSize}`,
      )
      .execute();
  },
  async updateTitle(companyId: string | number, conversationId: string, title: string) {
    return new Runtime<void>()
      .open('PUT', `/company/${companyId}/ai-assistant/conversation/${conversationId}/title`)
      .addRequestBody({ title })
      .execute();
  },
  async listMessages(companyId: string | number, conversationId: string) {
    return new Runtime<AiMessage[]>()
      .open('GET', `/company/${companyId}/ai-assistant/conversation/${conversationId}/messages`)
      .execute();
  },
  /** 申请导出 PDF：无导出权限时服务端返回 403，拿不到水印就不导出 */
  async createExportTicket(companyId: string | number, conversationId: string) {
    return new Runtime<AiExportTicket>()
      .open('POST', `/company/${companyId}/ai-assistant/conversation/${conversationId}/export`)
      .execute();
  },
};

/** 全员对话记录的列表行（仅 superadmin 可读），用量字段允许为空（老数据没有） */
export interface AiAdminConversation {
  id: string;
  title: string;
  userId?: number | string | null;
  userName?: string | null;
  departmentName?: string | null;
  deleted?: boolean;
  createdAt?: string;
  lastMessageAt?: string;
  messageCount?: number | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  cachedTokens?: number | null;
  durationMs?: number | null;
  models?: string[];
}

export interface AiAdminConversationPage {
  total: number;
  size: number;
  page: number;
  list: AiAdminConversation[];
}

/** 与列表同筛选口径的合计 */
export interface AiAdminUsageSummary {
  conversationCount?: number | null;
  userCount?: number | null;
  messageCount?: number | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  cachedTokens?: number | null;
  durationMs?: number | null;
  models?: string[];
}

export interface AiAdminConversationQuery {
  current?: number;
  pageSize?: number;
  userId?: number | string;
  keyword?: string;
  /** 最近活跃时间区间，ISO 本地时间字符串 */
  startAt?: string;
  endAt?: string;
  includeDeleted?: boolean;
}

function adminQueryString(query: AiAdminConversationQuery): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const search = params.toString();
  return search ? `?${search}` : '';
}

export const aiAssistantAdminApi = {
  async listConversations(companyId: string | number, query: AiAdminConversationQuery = {}) {
    return new Runtime<AiAdminConversationPage>()
      .open(
        'GET',
        `/company/${companyId}/ai-assistant/admin/conversations${adminQueryString(query)}`,
      )
      .execute();
  },
  async summary(companyId: string | number, query: AiAdminConversationQuery = {}) {
    // 合计与列表同条件，但与分页无关
    const { current, pageSize, ...rest } = query;
    return new Runtime<AiAdminUsageSummary>()
      .open(
        'GET',
        `/company/${companyId}/ai-assistant/admin/conversations/summary${adminQueryString(rest)}`,
      )
      .execute();
  },
  async listMessages(companyId: string | number, conversationId: string) {
    return new Runtime<AiMessage[]>()
      .open(
        'GET',
        `/company/${companyId}/ai-assistant/admin/conversation/${conversationId}/messages`,
      )
      .execute();
  },
  /** 软删除会话：全站唯一的删除入口，仅超管可用 */
  async deleteConversation(companyId: string | number, conversationId: string) {
    return new Runtime<void>()
      .open('DELETE', `/company/${companyId}/ai-assistant/admin/conversation/${conversationId}`)
      .execute();
  },
};

/** @ 员工候选/回查条目（手机号只有脱敏形式，服务端按数据范围收敛） */
export interface AiMentionUser {
  userId: number | string;
  name: string;
  departmentName?: string | null;
  roleName?: string | null;
  state?: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  mobileMasked?: string | null;
}

export const aiMentionApi = {
  /** 关键字搜索候选：名字 / 手机号 / 纯数字 userId；空关键字返回范围内默认候选 */
  async search(companyId: string | number, keyword: string) {
    const query = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
    return new Runtime<AiMentionUser[]>()
      .open('GET', `/company/${companyId}/ai-assistant/mention-users${query}`)
      .execute();
  },
  /** 按 userId 批量回查（hover 信息卡）；范围外的 id 服务端静默剔除 */
  async lookup(companyId: string | number, userIds: Array<string | number>) {
    return new Runtime<AiMentionUser[]>()
      .open('GET', `/company/${companyId}/ai-assistant/mention-users?userIds=${userIds.join(',')}`)
      .execute();
  },
};

/** 客户提及（customer://）的 hover 信息卡条目，手机号同样只有脱敏形式 */
export interface AiMentionCustomer {
  customerId: number | string;
  name: string;
  star?: number | null;
  state?: string | null;
  pool?: string | null;
  from?: string | null;
  channel?: string | null;
  ownerUserId?: number | string | null;
  ownerUserName?: string | null;
  mobileMasked?: string | null;
  loanCount?: number | null;
  loanSumYuan?: number | null;
  createdAt?: string | null;
  lastEventAt?: string | null;
}

export const aiMentionCustomerApi = {
  /** 关键字搜索候选：姓名 / 手机号 / 纯数字 customerId；空关键字返回最近有跟进的客户 */
  async search(companyId: string | number, keyword: string) {
    const query = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
    return new Runtime<AiMentionCustomer[]>()
      .open('GET', `/company/${companyId}/ai-assistant/mention-customers${query}`)
      .execute();
  },
  /** 按 customerId 批量回查；范围外的 id 服务端静默剔除 */
  async lookup(companyId: string | number, customerIds: Array<string | number>) {
    return new Runtime<AiMentionCustomer[]>()
      .open(
        'GET',
        `/company/${companyId}/ai-assistant/mention-customers?customerIds=${customerIds.join(',')}`,
      )
      .execute();
  },
};

/** 部门提及（department://）候选与 hover 信息卡条目；仅管理层可见 */
export interface AiMentionDepartment {
  departmentId: number | string;
  name: string;
  parentName?: string | null;
  /** 数据范围内的在职成员数 */
  memberCount?: number | null;
}

export const aiMentionDepartmentApi = {
  /** 关键字搜索候选；数据范围为「仅本人」的用户恒返回空列表 */
  async search(companyId: string | number, keyword: string) {
    const query = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
    return new Runtime<AiMentionDepartment[]>()
      .open('GET', `/company/${companyId}/ai-assistant/mention-departments${query}`)
      .execute();
  },
  /** 按 departmentId 批量回查（hover 信息卡）；范围外的 id 服务端静默剔除 */
  async lookup(companyId: string | number, departmentIds: Array<string | number>) {
    return new Runtime<AiMentionDepartment[]>()
      .open(
        'GET',
        `/company/${companyId}/ai-assistant/mention-departments?departmentIds=${departmentIds.join(',')}`,
      )
      .execute();
  },
};

export interface ChatStreamCallbacks {
  /** 新会话创建（仅首轮下发） */
  onConversation?: (conversationId: string, title: string) => void;
  onMessageStart?: (messageId: string) => void;
  onDelta?: (content: string) => void;
  /** 深度思考增量（仅开启思考的模型会下发） */
  onReasoning?: (content: string) => void;
  /** 技能搜索完成：total=配置技能总数，matched=启用命中数，names=命中技能名（仅首轮下发） */
  onSkillSearch?: (total: number, matched: number, names: string[]) => void;
  /** 技能加载完成：注入本次对话上下文的技能清单（仅首轮下发，且命中数 > 0 才有） */
  onSkillLoad?: (skills: Array<{ name: string; description?: string }>) => void;
  /** agent 开始执行某个工具（arguments 为模型给出的入参 JSON 字符串） */
  onToolCall?: (toolCallId: string, name: string, args: string) => void;
  /** 某个工具执行完成（summary 为结果摘要） */
  onToolResult?: (toolCallId: string, name: string, summary: string) => void;
  /** 图表 spec（JSON 字符串，单个） */
  onChart?: (chartJson: string) => void;
  onDone?: (messageId: string) => void;
  /** 服务端主动下发的失败事件 */
  onError?: (message: string) => void;
}

function dispatchSseBlock(block: string, callbacks: ChatStreamCallbacks) {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith(':')) continue; // 心跳注释
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    }
  }
  if (!dataLines.length) return;
  let data: any;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return;
  }
  switch (event) {
    case 'conversation':
      callbacks.onConversation?.(String(data.conversationId), data.title);
      break;
    case 'message_start':
      callbacks.onMessageStart?.(String(data.messageId));
      break;
    case 'message_delta':
      callbacks.onDelta?.(data.content ?? '');
      break;
    case 'reasoning_delta':
      callbacks.onReasoning?.(data.content ?? '');
      break;
    case 'skill_search':
      callbacks.onSkillSearch?.(
        Number(data.total) || 0,
        Number(data.matched) || 0,
        Array.isArray(data.names) ? data.names.map(String) : [],
      );
      break;
    case 'skill_load':
      callbacks.onSkillLoad?.(
        Array.isArray(data.skills) ? data.skills.filter((skill: any) => skill && skill.name) : [],
      );
      break;
    case 'tool_call':
      callbacks.onToolCall?.(data.toolCallId ?? '', data.name ?? '', data.arguments ?? '');
      break;
    case 'tool_result':
      callbacks.onToolResult?.(data.toolCallId ?? '', data.name ?? '', data.summary ?? '');
      break;
    case 'chart':
      callbacks.onChart?.(dataLines.join('\n'));
      break;
    case 'done':
      callbacks.onDone?.(String(data.messageId));
      break;
    case 'error':
      callbacks.onError?.(data.message ?? 'AI 回复失败');
      break;
    default:
      break;
  }
}

/**
 * 发起对话并消费 SSE 流，直到服务端关闭连接或 signal 中止。
 * HTTP 非 2xx 时抛出带后端 message 的 Error。
 */
export async function chatStream(
  companyId: string | number,
  body: { conversationId?: string; content: string },
  callbacks: ChatStreamCallbacks,
  signal?: AbortSignal,
) {
  const response = await fetch(`${apiHost}/company/${companyId}/ai-assistant/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: sessionStorage.getItem('token') ?? '',
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok || !response.body) {
    let msg = 'AI 回复失败，请稍后重试';
    try {
      const errBody = await response.json();
      msg = errBody?.message || msg;
    } catch {
      // 非 JSON 响应，用默认文案
    }
    throw new Error(msg);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const drainBuffer = () => {
    // SSE 规范允许 CRLF / LF / CR 行结束符，统一按空行切事件块
    const separator = /\r\n\r\n|\n\n|\r\r/;
    let match = separator.exec(buffer);
    while (match) {
      const block = buffer.slice(0, match.index);
      buffer = buffer.slice(match.index + match[0].length);
      dispatchSseBlock(block.replace(/\r\n|\r/g, '\n'), callbacks);
      match = separator.exec(buffer);
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    drainBuffer();
  }
  // EOF：flush decoder 余量并处理没有尾随空行的最后一个事件块
  buffer += decoder.decode();
  drainBuffer();
  if (buffer.trim()) {
    dispatchSseBlock(buffer.replace(/\r\n|\r/g, '\n'), callbacks);
  }
}
